import { createContext, PropsWithChildren, useState } from 'react';
import { useQuery } from 'react-query';

import { useUserConsent } from '@src/features/user-consent/hooks';
import { TripDetailsPage } from '@src/generated/dato-cms';
import { segmentService } from '@src/services';
import { ProfileWithMetadata, Trip } from '@src/types/api/models';
import { Brand } from '@src/types/content/brand';

type AnalyticsContextType = {
  segmentLoaded: boolean;
  segmentId: string;
  profile?: ProfileWithMetadata | null;
  trip?: Trip | null;
  property?: TripDetailsPage['property'];
  unitGroup?: TripDetailsPage['unitGroup'];
  updateProfile: (profile?: ProfileWithMetadata) => void;
  updateTrip: (trip?: Trip) => void;
};

type AnalyticsProviderProps = PropsWithChildren<{
  segmentId: string;
  brand: Brand;
  currentTrip?: Trip | null;
  profile?: ProfileWithMetadata | null;
  property?: AnalyticsContextType['property'];
  unitGroup?: AnalyticsContextType['unitGroup'];
}>;

export const AnalyticsContext = createContext<AnalyticsContextType>({
  segmentLoaded: false,
  segmentId: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateProfile: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateTrip: () => {},
});

export const AnalyticsProvider = ({
  children,
  segmentId,
  brand,
  profile: currentProfile,
  currentTrip,
  property,
  unitGroup,
}: AnalyticsProviderProps) => {
  const { consentStatus } = useUserConsent();
  const [trip, setTrip] = useState(currentTrip);
  const [profile, setProfile] = useState(currentProfile);

  const { isSuccess: segmentLoaded } = useQuery(
    'load-segment',
    () => segmentService.load(segmentId, consentStatus, brand),
    {
      enabled: !!consentStatus.Segment,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  return (
    <AnalyticsContext.Provider
      value={{
        segmentLoaded,
        segmentId,
        profile,
        trip,
        property,
        unitGroup,
        updateProfile: setProfile,
        updateTrip: setTrip,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
